
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useBrand } from '../hooks/useBrand'
import { StarDisplay, StarInput, RatingBreakdown } from '../components/StarRating'

const NAVY   = '#0B1F3A'
const TEAL   = '#2DD4B0'
const TEAL_D = '#1BA888'
const AMBER  = '#F59E0B'

function getUsername(s) { return s?.facebook_username || s?.facebook_url?.split('/').filter(Boolean).pop() || '' }
function waLink(phone) { return phone ? 'https://wa.me/' + phone.replace(/\D/g, '') : null }
function getCategories(s) {
  try { const bt = s?.business_types ? JSON.parse(s.business_types) : null; if (bt?.length) return bt } catch {}
  return s?.category ? [s.category] : []
}

const RATING_LABELS = { 5: 'Excellent', 4: 'Very good', 3: 'Good', 2: 'Fair', 1: 'Poor' }

export default function SellerProfile() {
  const { username }  = useParams()
  const navigate      = useNavigate()
  const { brand }     = useBrand()

  const [seller, setSeller]         = useState(null)
  const [reviews, setReviews]       = useState([])
  const [loading, setLoading]       = useState(true)
  const [activeTab, setActiveTab]   = useState('shop')
  const [saved, setSaved]           = useState(false)
  const [customerAccount, setCustomerAccount] = useState(null)
  const [myReview, setMyReview]     = useState(null)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [reviewForm, setReviewForm] = useState({ rating: 0, review_text: '' })
  const [submittingReview, setSubmittingReview] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [reviewSort, setReviewSort] = useState('recent')
  const [reviewFilter, setReviewFilter] = useState(0) // 0 = all
  const [linkCopied, setLinkCopied] = useState(false)

  useEffect(() => {
    fetchSeller()
    checkCustomerAuth()
    trackView()
  }, [username]) // eslint-disable-line

  const fetchSeller = async () => {
    setLoading(true)
    // Try by facebook_username first, then by id
    let { data } = await supabase.from('sellers')
      .select('*').eq('facebook_username', username).eq('verification_status', 'verified').single()
    if (!data) {
      const res = await supabase.from('sellers')
        .select('*').eq('id', username).eq('verification_status', 'verified').single()
      data = res.data
    }
    if (!data) { navigate('/sellers'); return }
    setSeller(data)
    await fetchReviews(data.id)
    setLoading(false)
  }

  const fetchReviews = async (sellerId) => {
    const { data } = await supabase.from('seller_reviews')
      .select('*, customer_accounts(name)')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
    setReviews(data || [])
  }

  const checkCustomerAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data: acc } = await supabase.from('customer_accounts').select('id, name').eq('user_id', user.id).single()
    if (!acc) return
    setCustomerAccount(acc)
  }

  useEffect(() => {
    if (!seller || !customerAccount) return
    // Check if already saved
    supabase.from('seller_followers').select('id').eq('seller_id', seller.id).eq('customer_account_id', customerAccount.id).single()
      .then(({ data }) => setSaved(!!data))
    // Check if already reviewed
    const existing = reviews.find(r => r.customer_account_id === customerAccount.id)
    if (existing) { setMyReview(existing); setReviewForm({ rating: existing.rating, review_text: existing.review_text || '' }) }
  }, [seller, customerAccount, reviews]) // eslint-disable-line

  const trackView = () => {
    if (!username) return
    try {
      // We'll update with seller.id once fetched — using username as placeholder
      const rv = JSON.parse(localStorage.getItem('pc_recent_sellers') || '[]')
      if (!rv.includes(username)) localStorage.setItem('pc_recent_sellers', JSON.stringify([username, ...rv].slice(0, 10)))
    } catch {}
  }

  const handleToggleSave = async () => {
    if (!customerAccount) { navigate('/customer/login'); return }
    if (saved) {
      await supabase.from('seller_followers').delete().eq('seller_id', seller.id).eq('customer_account_id', customerAccount.id)
      setSaved(false)
    } else {
      await supabase.from('seller_followers').insert({ seller_id: seller.id, customer_account_id: customerAccount.id })
      setSaved(true)
    }
  }

  const handleSubmitReview = async () => {
    if (reviewForm.rating === 0) { setReviewError('Please select a rating'); return }
    if (!customerAccount) { navigate('/customer/login'); return }
    setSubmittingReview(true); setReviewError('')

    // Check if customer has an order from this seller (verified purchase)
    const { data: orders } = await supabase.from('orders')
      .select('id, customer_id')
      .eq('seller_id', seller.id)
      .limit(1)

    // Get customer IDs for this account
    const { data: customers } = await supabase.from('customers')
      .select('id').eq('seller_id', seller.id)

    const customerIds = customers?.map(c => c.id) || []
    const hasOrder = orders?.some(o => customerIds.includes(o.customer_id)) || false

    const payload = {
      seller_id: seller.id,
      customer_account_id: customerAccount.id,
      customer_name: customerAccount.name || 'Customer',
      rating: reviewForm.rating,
      review_text: reviewForm.review_text.trim() || null,
      is_verified_purchase: hasOrder,
    }

    if (myReview) {
      await supabase.from('seller_reviews').update({ rating: payload.rating, review_text: payload.review_text, updated_at: new Date().toISOString() }).eq('id', myReview.id)
    } else {
      await supabase.from('seller_reviews').insert(payload)
    }

    await fetchReviews(seller.id)
    // Refresh seller rating
    const { data: updatedSeller } = await supabase.from('sellers').select('avg_rating, review_count').eq('id', seller.id).single()
    if (updatedSeller) setSeller(s => ({ ...s, ...updatedSeller }))
    setShowReviewForm(false)
    setSubmittingReview(false)
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href)
    setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000)
  }

  const sortedReviews = [...reviews]
    .filter(r => reviewFilter === 0 || r.rating === reviewFilter)
    .sort((a, b) => {
      if (reviewSort === 'recent')  return new Date(b.created_at) - new Date(a.created_at)
      if (reviewSort === 'highest') return b.rating - a.rating
      if (reviewSort === 'lowest')  return a.rating - b.rating
      return b.helpful_count - a.helpful_count
    })

  const cats = getCategories(seller)
  const wa   = waLink(seller?.phone)

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', fontFamily: 'Space Grotesk, sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid ' + TEAL, borderTopColor: 'transparent', animation: 'sp-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
        <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Loading seller...</div>
      </div>
      <style>{`@keyframes sp-spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )

  if (!seller) return null

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>
      <style>{`
        @keyframes sp-spin { to { transform: rotate(360deg) } }
        @keyframes sp-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .sp-fade { animation: sp-fade 0.3s ease-out both; }
        .sp-tab-btn { padding: 10px 20px; border: none; background: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; transition: all 0.15s; white-space: nowrap; }
        .sp-review-card { background: #fff; border: 1px solid #E5E7EB; border-radius: 14px; padding: 16px; transition: box-shadow 0.15s; }
        .sp-review-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.06); }
        @media(max-width:768px) { .sp-header-inner { flex-direction: column !important; align-items: flex-start !important; } .sp-actions { flex-wrap: wrap; } }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{ background: NAVY, padding: '0 20px', height: '52px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 2px 12px rgba(11,31,58,0.3)' }}>
        <Link to="/sellers" style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12.5px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontWeight: 600 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          Directory
        </Link>
        <span style={{ color: 'rgba(255,255,255,0.2)' }}>›</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seller.shop_name}</span>
        <div style={{ marginLeft: 'auto' }}>
          {brand.logo_light_url
            ? <img src={brand.logo_light_url} alt={brand.name} style={{ height: '22px', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            : <span style={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>{brand.name}</span>
          }
        </div>
      </nav>

      {/* ── PROFILE HEADER ── */}
      <div style={{ background: NAVY, padding: '24px 20px 0' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="sp-header-inner" style={{ display: 'flex', alignItems: 'flex-end', gap: '20px', paddingBottom: '20px' }}>
            {/* Logo */}
            <div style={{ width: '88px', height: '88px', borderRadius: '18px', overflow: 'hidden', background: '#fff', border: '3px solid rgba(255,255,255,0.2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {seller.logo_url
                ? <img src={seller.logo_url} alt={seller.shop_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="rgba(45,212,176,0.5)" strokeWidth="1.5" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18"/></svg>
              }
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Name + verified */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>{seller.shop_name}</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(45,212,176,0.15)', border: '1px solid rgba(45,212,176,0.3)', borderRadius: '20px', padding: '3px 10px' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: TEAL }}>Verified Seller</span>
                </div>
              </div>

              {/* Tagline */}
              {seller.tagline && (
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', marginBottom: '8px' }}>{seller.tagline}</div>
              )}

              {/* Meta row */}
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Rating */}
                {seller.avg_rating > 0 && (
                  <button onClick={() => setActiveTab('reviews')} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
                    <StarDisplay rating={seller.avg_rating} size={13} />
                    <span style={{ fontSize: '13px', fontWeight: 700, color: AMBER }}>{Number(seller.avg_rating).toFixed(1)}</span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>({seller.review_count} review{seller.review_count !== 1 ? 's' : ''})</span>
                  </button>
                )}
                {/* Location */}
                <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {seller.seller_mode === 'international' ? (seller.country || 'International') : (seller.district || 'Bangladesh')}
                </div>
                {/* Categories */}
                {cats.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)' }}>{cats.slice(0, 3).join(' · ')}</div>
                )}
                {/* Member since */}
                {seller.member_since && (
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
                    Member since {new Date(seller.member_since).getFullYear()}
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="sp-actions" style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {wa && (
                <a href={wa} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '10px', background: '#25D366', fontSize: '13px', fontWeight: 700, color: '#fff', textDecoration: 'none' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
              )}
              {seller.facebook_url && (
                <a href={seller.facebook_url} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '10px', background: '#1877F2', fontSize: '13px', fontWeight: 700, color: '#fff', textDecoration: 'none' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                  Facebook
                </a>
              )}
              <button onClick={handleToggleSave}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 14px', borderRadius: '10px', background: saved ? '#FEF2F2' : 'rgba(255,255,255,0.12)', border: '1px solid', borderColor: saved ? '#FECACA' : 'rgba(255,255,255,0.2)', fontSize: '13px', fontWeight: 600, color: saved ? '#EF4444' : 'rgba(255,255,255,0.8)', cursor: 'pointer', fontFamily: 'inherit' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill={saved ? '#EF4444' : 'none'} stroke={saved ? '#EF4444' : 'currentColor'} strokeWidth="2" strokeLinecap="round"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
                {saved ? 'Saved' : 'Save'}
              </button>
              <button onClick={handleCopyLink}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 14px', borderRadius: '10px', background: linkCopied ? 'rgba(45,212,176,0.2)' : 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', fontSize: '13px', fontWeight: 600, color: linkCopied ? TEAL : 'rgba(255,255,255,0.7)', cursor: 'pointer', fontFamily: 'inherit' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                {linkCopied ? 'Copied!' : 'Share'}
              </button>
            </div>
          </div>

          {/* ── Tab bar ── */}
          <div style={{ display: 'flex', gap: '2px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {[
              { key: 'shop',    label: 'Shop',    ic: 'M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0' },
              { key: 'reviews', label: `Reviews${reviews.length > 0 ? ` (${reviews.length})` : ''}`, ic: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z' },
              { key: 'about',   label: 'About',   ic: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8z' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} className="sp-tab-btn"
                style={{ color: activeTab === tab.key ? '#fff' : 'rgba(255,255,255,0.45)', borderBottom: '2px solid', borderColor: activeTab === tab.key ? TEAL : 'transparent', paddingBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d={tab.ic}/></svg>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── TAB CONTENT ── */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>

        {/* ── SHOP TAB — embedded ShopPage via iframe approach ── */}
        {activeTab === 'shop' && (
          <div className="sp-fade">
            {getUsername(seller) ? (
              <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                <div style={{ padding: '12px 18px', borderBottom: '1px solid #E5E7EB', background: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22C55E' }} />
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#374151' }}>Live shop — {seller.shop_name}</span>
                  </div>
                  <a href={'/web/' + getUsername(seller)} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '12px', fontWeight: 600, color: TEAL_D, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Open full page
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
                  </a>
                </div>
                <iframe
                  src={'/web/' + getUsername(seller)}
                  title={seller.shop_name + ' shop'}
                  style={{ width: '100%', height: '680px', border: 'none', display: 'block' }}
                  loading="lazy" />
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '40px', textAlign: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.2" strokeLinecap="round" style={{ margin: '0 auto 14px', display: 'block' }}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18"/></svg>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>Shop page not set up yet</div>
                <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '16px' }}>This seller hasn't set up their PageConnect shop page yet.</div>
                {seller.facebook_url && (
                  <a href={seller.facebook_url} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 20px', borderRadius: '10px', background: '#1877F2', color: '#fff', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
                    Visit Facebook page instead →
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── REVIEWS TAB ── */}
        {activeTab === 'reviews' && (
          <div className="sp-fade">
            {/* Rating summary */}
            <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '24px', marginBottom: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              {reviews.length > 0 ? (
                <RatingBreakdown reviews={reviews} />
              ) : (
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  <StarDisplay rating={0} size={20} />
                  <div style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '8px' }}>No reviews yet — be the first!</div>
                </div>
              )}
            </div>

            {/* Write review CTA */}
            {customerAccount ? (
              <div style={{ marginBottom: '14px' }}>
                {!showReviewForm ? (
                  <button onClick={() => setShowReviewForm(true)}
                    style={{ width: '100%', padding: '13px', borderRadius: '12px', border: '1.5px dashed ' + TEAL, background: 'rgba(45,212,176,0.04)', fontSize: '13.5px', fontWeight: 600, color: TEAL_D, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    {myReview ? 'Edit your review' : 'Write a review'}
                  </button>
                ) : (
                  <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid ' + TEAL, padding: '20px', boxShadow: '0 2px 12px rgba(45,212,176,0.1)' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#111827', marginBottom: '16px' }}>
                      {myReview ? 'Edit your review' : 'Write a review for ' + seller.shop_name}
                    </div>

                    {/* Star selector */}
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '8px' }}>Your rating *</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <StarInput value={reviewForm.rating} onChange={v => setReviewForm(f => ({ ...f, rating: v }))} size={36} />
                        {reviewForm.rating > 0 && (
                          <span style={{ fontSize: '14px', fontWeight: 600, color: AMBER }}>{RATING_LABELS[reviewForm.rating]}</span>
                        )}
                      </div>
                    </div>

                    {/* Review text */}
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', marginBottom: '6px' }}>Your review <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span></div>
                      <textarea value={reviewForm.review_text} onChange={e => setReviewForm(f => ({ ...f, review_text: e.target.value }))}
                        placeholder="Tell others about your experience with this seller — product quality, communication, delivery speed..."
                        rows={4}
                        style={{ width: '100%', border: '1.5px solid #E5E7EB', borderRadius: '10px', padding: '11px 14px', fontSize: '13.5px', outline: 'none', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', color: '#111827', lineHeight: 1.6 }}
                        onFocus={e => e.target.style.borderColor = TEAL}
                        onBlur={e => e.target.style.borderColor = '#E5E7EB'} />
                    </div>

                    {reviewError && (
                      <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12.5px', color: '#DC2626', marginBottom: '12px' }}>{reviewError}</div>
                    )}

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => { setShowReviewForm(false); setReviewError('') }}
                        style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #E5E7EB', background: '#fff', fontSize: '13px', fontWeight: 600, color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit' }}>
                        Cancel
                      </button>
                      <button onClick={handleSubmitReview} disabled={submittingReview || reviewForm.rating === 0}
                        style={{ flex: 2, padding: '11px', borderRadius: '10px', border: 'none', background: reviewForm.rating > 0 ? NAVY : '#E5E7EB', fontSize: '13px', fontWeight: 700, color: reviewForm.rating > 0 ? '#fff' : '#9CA3AF', cursor: reviewForm.rating > 0 ? 'pointer' : 'not-allowed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        {submittingReview && <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'sp-spin 0.7s linear infinite' }} />}
                        {submittingReview ? 'Submitting...' : myReview ? 'Update review' : 'Submit review'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', padding: '16px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ fontSize: '13px', color: '#6B7280' }}>Sign in to write a review</div>
                <Link to="/customer/login" style={{ padding: '8px 16px', borderRadius: '8px', background: NAVY, color: '#fff', fontSize: '12.5px', fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
              </div>
            )}

            {/* Review filters */}
            {reviews.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {[{v:0,l:'All'},{v:5,l:'5★'},{v:4,l:'4★'},{v:3,l:'3★'},{v:2,l:'2★'},{v:1,l:'1★'}].map(f => (
                    <button key={f.v} onClick={() => setReviewFilter(f.v)}
                      style={{ padding: '5px 12px', borderRadius: '20px', border: '1px solid', borderColor: reviewFilter === f.v ? NAVY : '#E5E7EB', background: reviewFilter === f.v ? NAVY : '#fff', color: reviewFilter === f.v ? '#fff' : '#6B7280', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {f.l}
                    </button>
                  ))}
                </div>
                <select value={reviewSort} onChange={e => setReviewSort(e.target.value)}
                  style={{ padding: '6px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px', fontFamily: 'inherit', background: '#fff', color: '#374151', outline: 'none', cursor: 'pointer' }}>
                  <option value="recent">Most recent</option>
                  <option value="highest">Highest rated</option>
                  <option value="lowest">Lowest rated</option>
                </select>
              </div>
            )}

            {/* Review list */}
            {sortedReviews.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '32px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                  {reviewFilter > 0 ? `No ${reviewFilter}-star reviews yet` : 'No reviews yet'}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {sortedReviews.map(review => (
                  <div key={review.id} className="sp-review-card">
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* Avatar */}
                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'linear-gradient(135deg, ' + NAVY + ', #0D2B4E)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{(review.customer_name || 'A').slice(0,1).toUpperCase()}</span>
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#111827' }}>{review.customer_name}</span>
                            {review.is_verified_purchase && (
                              <span style={{ fontSize: '10.5px', fontWeight: 700, color: TEAL_D, background: 'rgba(45,212,176,0.08)', border: '1px solid rgba(45,212,176,0.2)', borderRadius: '20px', padding: '1px 7px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                                Verified purchase
                              </span>
                            )}
                            {review.customer_account_id === customerAccount?.id && (
                              <span style={{ fontSize: '10.5px', color: '#9CA3AF' }}>(your review)</span>
                            )}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginTop: '1px' }}>
                            {new Date(review.created_at).toLocaleDateString('en-BD', { day: 'numeric', month: 'long', year: 'numeric' })}
                            {review.updated_at !== review.created_at && ' · edited'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <StarDisplay rating={review.rating} size={13} />
                        <span style={{ fontSize: '12px', fontWeight: 700, color: AMBER }}>{review.rating}</span>
                      </div>
                    </div>

                    {review.review_text && (
                      <p style={{ fontSize: '13.5px', color: '#374151', lineHeight: 1.7, margin: '0 0 10px' }}>{review.review_text}</p>
                    )}

                    {!review.review_text && (
                      <div style={{ fontSize: '13px', color: '#9CA3AF', fontStyle: 'italic', marginBottom: '10px' }}>
                        {RATING_LABELS[review.rating]} — no written review
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ABOUT TAB ── */}
        {activeTab === 'about' && (
          <div className="sp-fade" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* About */}
            {(seller.about_long || seller.about_short) && (
              <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px' }}>About</div>
                <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.8, margin: 0 }}>
                  {seller.about_long || seller.about_short}
                </p>
              </div>
            )}

            {/* Details grid */}
            <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Details</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'Categories', value: cats.length > 0 ? cats.join(', ') : null },
                  { label: 'Location', value: seller.seller_mode === 'international' ? (seller.country || 'International') : [seller.district, 'Bangladesh'].filter(Boolean).join(', ') },
                  { label: 'Delivery', value: seller.delivery_coverage === 'nationwide' ? 'Ships nationwide' : seller.delivery_coverage === 'international' ? 'Ships internationally' : seller.delivery_coverage === 'local' ? 'Local delivery only' : null },
                  { label: 'Price range', value: seller.price_range ? seller.price_range.charAt(0).toUpperCase() + seller.price_range.slice(1) : null },
                  { label: 'Member since', value: seller.member_since ? new Date(seller.member_since).toLocaleDateString('en-BD', { month: 'long', year: 'numeric' }) : seller.created_at ? new Date(seller.created_at).toLocaleDateString('en-BD', { month: 'long', year: 'numeric' }) : null },
                  { label: 'Verified', value: 'Identity verified by PageConnect' },
                  { label: 'Followers', value: seller.follower_count > 0 ? seller.follower_count + ' people saved this shop' : null },
                ].filter(d => d.value).map(d => (
                  <div key={d.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '8px 0', borderBottom: '1px solid #F9FAFB' }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: '#9CA3AF', minWidth: '100px', flexShrink: 0 }}>{d.label}</span>
                    <span style={{ fontSize: '13px', color: '#374151' }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social links */}
            {(seller.facebook_url || wa || seller.instagram_url) && (
              <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '12px' }}>Contact & Social</div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {wa && <a href={wa} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 16px', borderRadius: '10px', background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: '13px', fontWeight: 600, color: '#15803D', textDecoration: 'none' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#15803D"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>}
                  {seller.facebook_url && <a href={seller.facebook_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 16px', borderRadius: '10px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '13px', fontWeight: 600, color: '#1D4ED8', textDecoration: 'none' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#1D4ED8"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                    Facebook page
                  </a>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}